import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { AdminInitialState } from './types';
import { currentSession } from '../services/auth/session.api';
import { readSession, clearSession } from './store';

/** 管理员会话及启动核验状态。 */
interface SessionContext {
  /** 已核验的管理员公开身份。 */
  initialState: AdminInitialState;
  /** 登录、退出和会话失效时更新身份。 */
  setInitialState: (value: AdminInitialState) => Promise<void>;
  /** 启动时的身份核验是否完成。 */
  ready: boolean;
}

/** 会话提供器接收的页面内容。 */
interface SessionProviderProps {
  /** 登录页和受保护的业务页面。 */
  children: ReactNode;
}

const Context = createContext<SessionContext | null>(null);

/** 读取当前管理员会话，禁止脱离提供器使用。 */
export function useAdminSession() {
  const value = useContext(Context);
  if (!value) throw new Error('缺少会话提供器');
  return value;
}

/** 启动时核验持久化会话，并向页面提供稳定的身份更新入口。 */
export function AdminSessionProvider({ children }: SessionProviderProps) {
  const [initialState, setState] = useState<AdminInitialState>({});
  const [ready, setReady] = useState(false);
  const setInitialState = useCallback(async (value: AdminInitialState) => { setState(value); }, []);
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        if (readSession()) {
          const session = await currentSession();
          if (active && session.account.role === 'ADMIN') setState({ currentSession: session });
          else if (active) clearSession();
        }
      } catch { /* 请求层处理失效会话，网络错误显示登录入口。 */ }
      finally { if (active) setReady(true); }
    })();
    return () => { active = false; };
  }, []);
  return <Context.Provider value={{ initialState, ready, setInitialState }}>{children}</Context.Provider>;
}
